import * as yup from 'yup';

import { getTaxIdRequirementMap, TaxIdRequirement } from '@arrived/common';
import { AccountType, Domicile, EntityType } from '@arrived/models';

import { addressSchema, birthDateSchema, phoneNumberSchema } from '../common';
import {
  getIsAccreditedInvestor,
  SuitabilityQuestionnaireSchema,
  suitabilityQuestionnaireSchema,
} from './suitabilityQuestionnaire.schema';

export type ValidAccountType = AccountType.INDIVIDUAL | AccountType.ENTITY | AccountType.CHECKBOOK_IRA;

export const accountsRequestSchema = yup
  .object({
    firstName: yup.string().trim().max(128).required(),
    lastName: yup.string().trim().max(128).required(),
    domicile: yup.mixed<Domicile>().oneOf([Domicile.US_CITIZEN, Domicile.US_RESIDENT]).required(),
    accountType: yup
      .mixed<AccountType>()
      .oneOf([AccountType.INDIVIDUAL, AccountType.ENTITY, AccountType.CHECKBOOK_IRA])
      .required(),
    entityType: yup
      .mixed<EntityType>()
      .oneOf(Object.values(EntityType))
      .when('accountType', (accountType: ValidAccountType, schema) => {
        switch (accountType) {
          case AccountType.INDIVIDUAL:
            return schema.notRequired().nullable().strip();
          case AccountType.ENTITY:
            return schema.required();
          case AccountType.CHECKBOOK_IRA:
            // There's an error with yup where we can't test .equals on an enum value so we have to do a test
            return schema.required().test({
              name: 'isLLC',
              test: (value: EntityType | undefined) => value === EntityType.LLC,
            });
        }
      }),
    entityName: yup
      .string()
      .trim()
      .when('accountType', (accountType: ValidAccountType, schema) => {
        switch (accountType) {
          case AccountType.INDIVIDUAL:
            return schema.notRequired().nullable().strip();
          case AccountType.ENTITY:
          case AccountType.CHECKBOOK_IRA:
            return schema.required();
        }
      }),
    entityTitle: yup
      .string()
      .trim()
      .max(120)
      .when('accountType', (accountType: ValidAccountType, schema) => {
        switch (accountType) {
          case AccountType.INDIVIDUAL:
            return schema.notRequired().nullable().strip();
          case AccountType.ENTITY:
          case AccountType.CHECKBOOK_IRA:
            return schema.required('account.entityTitle.required');
        }
      }),
    residentialAddress: addressSchema.required(),
    isMailingAddressSame: yup.boolean().required(),
    mailingAddress: addressSchema.when('isMailingAddressSame', (isMailingAddressSame: boolean, schema) => {
      return isMailingAddressSame ? schema.notRequired().nullable().strip() : schema.required();
    }),
    phoneNumber: phoneNumberSchema.required(),
    birthDate: birthDateSchema.required(),
    ssn: yup
      .string()
      .trim()
      .matches(/^\d{3}-\d{2}-\d{4}$/, 'account.ssn.invalid')
      .required('account.ssn.required'),
    ein: yup
      .string()
      .trim()
      .matches(/^\d{2}-\d{7}$/, 'account.ein.invalid')
      // @ts-ignore Yup seems to have a typing issue here and TS will validate this when the function is
      // ([accountType, entityType], schema) => { ... } but that results in actual issues+
      .when(['accountType', 'entityType'], (accountType: ValidAccountType, entityType?: EntityType, schema) => {
        const taxIdRequirementMap = getTaxIdRequirementMap(accountType, entityType);
        switch (taxIdRequirementMap?.EIN) {
          case TaxIdRequirement.REQUIRED:
            return schema.required();
          case TaxIdRequirement.OPTIONAL:
            return schema.notRequired().nullable();
          case TaxIdRequirement.NONE:
          default:
            return schema.notRequired().nullable().strip();
        }
      })
      // When the EIN is empty, set it to null to satisfy `nullable`
      .transform((v, o) => (o === '' ? null : v)),
    suitabilityQuestionnaire: suitabilityQuestionnaireSchema.required(),
    investResponsibly: yup
      .boolean()
      .when('suitabilityQuestionnaire', (suitabilityQuestionnaire: SuitabilityQuestionnaireSchema, schema) => {
        const isAccreditedInvestor = getIsAccreditedInvestor(suitabilityQuestionnaire);
        return isAccreditedInvestor ? schema.notRequired().nullable().strip() : schema.required().oneOf([true]);
      }),
  })
  .defined();

export type AccountsRequestSchema = yup.InferType<typeof accountsRequestSchema>;
